/**
 * Base API Service
 * Handles all API requests to the backend server
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4901/apiv1';

// Get base path for redirect URLs (handles subdirectory deployments)
declare const __BASE_PATH__: string;
const BASE_PATH = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/';

interface ApiResponse<T = any> {
  status: number;
  message?: string;
  data?: T;
  error?: any;
  token?: string; // x-access-token from response headers
}

class BaseApiService {
  private baseURL: string;
  private isRedirecting: boolean = false;
  private redirectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Get the login URL with proper base path
   */
  private getLoginUrl(): string {
    const basePath = BASE_PATH.replace(/\/$/, ''); // Remove trailing slash
    return `${basePath}/citizen-login`;
  }

  /**
   * Handle session expiration - clear storage and redirect to login
   */
  private handleSessionExpired(_message?: string): void {
    // Prevent multiple redirects within 2 seconds
    if (this.isRedirecting) {
      return;
    }

    this.isRedirecting = true;

    // Reset flag after 2 seconds to allow future redirects if needed
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
    this.redirectTimeout = setTimeout(() => {
      this.isRedirecting = false;
    }, 2000);

    // Clear all authentication data
    localStorage.removeItem('x-access-token');
    localStorage.removeItem('token');
    localStorage.removeItem('citizenName');
    localStorage.removeItem('citizenPhone');
    localStorage.removeItem('citizenPhoneNumber');
    localStorage.removeItem('citizenId');

    // Clear session storage if any
    sessionStorage.removeItem('x-access-token');
    sessionStorage.removeItem('token');
    sessionStorage.clear();

    // Redirect to login page
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const loginUrl = this.getLoginUrl();

      // Only redirect if not already on login page
      if (!currentPath.includes('/citizen-login')) {
        console.log('Session expired, redirecting to:', loginUrl);
        // Use replace to prevent back button from going to expired session
        window.location.replace(loginUrl);
      } else {
        // Reset flag if already on login page
        this.isRedirecting = false;
      }
    }
  }

  /**
   * Reset redirect flag (useful for testing or manual resets)
   */
  public resetRedirectFlag(): void {
    this.isRedirecting = false;
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
      this.redirectTimeout = null;
    }
  }

  /**
   * Check if response indicates session expiration
   */
  private isSessionExpired(status: number, data: any): boolean {
    // Check for common unauthorized/forbidden status codes
    // 401 = Unauthorized, 403 = Forbidden/Session Expired, 406 = Not Acceptable (auth2 uses this)
    if (status === 401 || status === 403 || status === 406) {
      console.log('[BaseApiService] Session expired: HTTP status', status);
      return true;
    }

    // Check response body status (some APIs return 200 HTTP but error status in body)
    const bodyStatus = data?.status;
    if (bodyStatus === 401 || bodyStatus === 403 || bodyStatus === 406) {
      console.log('[BaseApiService] Session expired: Body status', bodyStatus);
      return true;
    }

    // Check for auth flag (server returns auth: false for session issues)
    if (data?.auth === false) {
      console.log('[BaseApiService] Session expired: auth flag is false');
      return true;
    }

    // Check for access flag (server may return access: false for permission issues)
    if (data?.access === false) {
      console.log('[BaseApiService] Session expired: access flag is false');
      return true;
    }

    // Check for specific error messages indicating session expiration
    const errorMessage = data?.message || data?.error?.message || data?.msg || '';
    const lowerMessage = String(errorMessage).toLowerCase();

    const sessionExpiredPatterns = [
      'session expired',
      'token expired',
      'unauthorized',
      'authentication failed',
      'invalid token',
      'jwt expired',
      'no token',
      'token required',
      'access denied',
      'no session',
      'please login',
      'please log in',
      'login again',
      'log in again'
    ];

    for (const pattern of sessionExpiredPatterns) {
      if (lowerMessage.includes(pattern)) {
        console.log('[BaseApiService] Session expired: Message pattern match:', pattern);
        return true;
      }
    }

    return false;
  }

  /**
   * Force logout and redirect to login page
   * Can be called from anywhere in the app to trigger session cleanup
   */
  public forceLogout(reason?: string): void {
    console.log('[BaseApiService] Force logout triggered:', reason || 'No reason provided');
    this.handleSessionExpired(reason);
  }

  /**
   * Get request
   */
  async get<T = any>(endpoint: string, config?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/${endpoint.replace(/^\//, '')}`;

      // Get stored token from localStorage
      const token = localStorage.getItem('x-access-token');

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Include cookies for session management
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'x-access-token': token }),
          ...config?.headers,
        },
        ...config,
      });

      // Parse JSON response once
      let data: any = {};
      try {
        data = await response.json();
      } catch {
        // If JSON parsing fails, check for unauthorized status
        if (response.status === 401 || response.status === 403 || response.status === 406) {
          this.handleSessionExpired();
          return {
            status: response.status,
            message: 'Session expired. Please login again.',
            error: { status: response.status },
          };
        }
        return {
          status: response.status,
          message: 'Invalid response format',
          error: { message: 'Response is not valid JSON' },
        };
      }

      // Check for session expiration (check both HTTP status and response body)
      if (this.isSessionExpired(response.status, data)) {
        this.handleSessionExpired(data.message || data.msg);
        return {
          status: data.status || response.status,
          message: data.message || data.msg || 'Session expired. Please login again.',
          error: data,
        };
      }

      if (!response.ok) {
        return {
          status: response.status,
          message: data.message || data.msg || 'Request failed',
          error: data,
        };
      }

      // Extract and store token from response headers
      const accessToken = response.headers.get('x-access-token') || undefined;
      if (accessToken) {
        localStorage.setItem('x-access-token', accessToken);
      }

      return {
        status: response.status,
        data: data.data || data,
        message: data.message || data.msg,
        token: accessToken,
      };
    } catch (error: any) {
      console.error('API GET Error:', error);

      // Check if it's a network error that might indicate session issues
      if (error.message && (error.message.includes('401') || error.message.includes('unauthorized'))) {
        this.handleSessionExpired();
      }

      return {
        status: 500,
        message: error.message || 'Network error',
        error,
      };
    }
  }

  /**
   * Post request
   */
  async post<T = any>(endpoint: string, body?: any, config?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/${endpoint.replace(/^\//, '')}`;

      // Get stored token from localStorage
      const token = localStorage.getItem('x-access-token');

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Include cookies for session management
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'x-access-token': token }),
          ...config?.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        // Don't spread config here as it might override body and headers
        ...(config && Object.keys(config).filter(key => key !== 'headers' && key !== 'body').reduce((acc, key) => {
          (acc as Record<string, unknown>)[key] = config[key as keyof RequestInit];
          return acc;
        }, {} as RequestInit)),
      });

      // Extract x-access-token from response headers
      const accessToken = response.headers.get('x-access-token') || undefined;

      if (accessToken) {
        // Store token in localStorage for future requests
        localStorage.setItem('x-access-token', accessToken);
      }

      // Parse JSON response once
      let data: any = {};
      try {
        data = await response.json();
      } catch {
        // If JSON parsing fails, check for unauthorized status
        if (response.status === 401 || response.status === 403 || response.status === 406) {
          this.handleSessionExpired();
          return {
            status: response.status,
            message: 'Session expired. Please login again.',
            error: { status: response.status },
            token: accessToken,
          };
        }
        return {
          status: response.status,
          message: 'Invalid response format',
          error: { message: 'Response is not valid JSON' },
          token: accessToken,
        };
      }

      // Check for session expiration (check both HTTP status and response body)
      if (this.isSessionExpired(response.status, data)) {
        this.handleSessionExpired(data.message || data.msg);
        return {
          status: data.status || response.status,
          message: data.message || data.msg || 'Session expired. Please login again.',
          error: data,
          token: accessToken,
        };
      }

      if (!response.ok) {
        return {
          status: response.status,
          message: data.message || data.msg || 'Request failed',
          error: data,
          token: accessToken,
        };
      }

      return {
        status: response.status,
        data: data.data || data,
        message: data.message || data.msg,
        token: accessToken,
      };
    } catch (error: any) {
      console.error('API POST Error:', error);

      // Check if it's a network error that might indicate session issues
      if (error.message && (error.message.includes('401') || error.message.includes('unauthorized'))) {
        this.handleSessionExpired();
      }

      return {
        status: 500,
        message: error.message || 'Network error',
        error,
      };
    }
  }

  /**
   * Put request
   */
  async put<T = any>(endpoint: string, body?: any, config?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/${endpoint.replace(/^\//, '')}`;

      // Get stored token from localStorage
      const token = localStorage.getItem('x-access-token');

      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include', // Include cookies for session management
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'x-access-token': token }),
          ...config?.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        ...config,
      });

      // Extract x-access-token from response headers
      const accessToken = response.headers.get('x-access-token') || undefined;

      if (accessToken) {
        localStorage.setItem('x-access-token', accessToken);
      }

      // Parse JSON response once
      let data: any = {};
      try {
        data = await response.json();
      } catch {
        // If JSON parsing fails, check for unauthorized status
        if (response.status === 401 || response.status === 403 || response.status === 406) {
          this.handleSessionExpired();
          return {
            status: response.status,
            message: 'Session expired. Please login again.',
            error: { status: response.status },
            token: accessToken,
          };
        }
        return {
          status: response.status,
          message: 'Invalid response format',
          error: { message: 'Response is not valid JSON' },
          token: accessToken,
        };
      }

      // Check for session expiration (check both HTTP status and response body)
      if (this.isSessionExpired(response.status, data)) {
        this.handleSessionExpired(data.message || data.msg);
        return {
          status: data.status || response.status,
          message: data.message || data.msg || 'Session expired. Please login again.',
          error: data,
          token: accessToken,
        };
      }

      if (!response.ok) {
        return {
          status: response.status,
          message: data.message || data.msg || 'Request failed',
          error: data,
          token: accessToken,
        };
      }

      return {
        status: response.status,
        data: data.data || data,
        message: data.message || data.msg,
        token: accessToken,
      };
    } catch (error: any) {
      console.error('API PUT Error:', error);

      // Check if it's a network error that might indicate session issues
      if (error.message && (error.message.includes('401') || error.message.includes('unauthorized'))) {
        this.handleSessionExpired();
      }

      return {
        status: 500,
        message: error.message || 'Network error',
        error,
      };
    }
  }

  /**
   * Delete request
   */
  async delete<T = any>(endpoint: string, config?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/${endpoint.replace(/^\//, '')}`;

      // Get stored token from localStorage
      const token = localStorage.getItem('x-access-token');

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include', // Include cookies for session management
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'x-access-token': token }),
          ...config?.headers,
        },
        ...config,
      });

      // Extract x-access-token from response headers
      const accessToken = response.headers.get('x-access-token') || undefined;

      if (accessToken) {
        localStorage.setItem('x-access-token', accessToken);
      }

      // Parse JSON response once
      let data: any = {};
      try {
        data = await response.json();
      } catch {
        // If JSON parsing fails, check for unauthorized status
        if (response.status === 401 || response.status === 403 || response.status === 406) {
          this.handleSessionExpired();
          return {
            status: response.status,
            message: 'Session expired. Please login again.',
            error: { status: response.status },
            token: accessToken,
          };
        }
        return {
          status: response.status,
          message: 'Invalid response format',
          error: { message: 'Response is not valid JSON' },
          token: accessToken,
        };
      }

      // Check for session expiration (check both HTTP status and response body)
      if (this.isSessionExpired(response.status, data)) {
        this.handleSessionExpired(data.message || data.msg);
        return {
          status: data.status || response.status,
          message: data.message || data.msg || 'Session expired. Please login again.',
          error: data,
          token: accessToken,
        };
      }

      if (!response.ok) {
        return {
          status: response.status,
          message: data.message || data.msg || 'Request failed',
          error: data,
          token: accessToken,
        };
      }

      return {
        status: response.status,
        data: data.data || data,
        message: data.message || data.msg,
        token: accessToken,
      };
    } catch (error: any) {
      console.error('API DELETE Error:', error);

      // Check if it's a network error that might indicate session issues
      if (error.message && (error.message.includes('401') || error.message.includes('unauthorized'))) {
        this.handleSessionExpired();
      }

      return {
        status: 500,
        message: error.message || 'Network error',
        error,
      };
    }
  }
}

// Export singleton instance
const service = new BaseApiService();
export default service;

